import React, { useState } from 'react';
import styled from 'styled-components';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { DisplayAlignment, DisplaySize, ModalType } from '../../shared/enum/EnumRepository';
import { useWorkViewStore } from '../../shared/store/WorkViewStore';
import OptimizedMoleculeImg from '../../shared/component/molecule/OptimizedMoleculeImg';
import { StyledImgProps } from '../../shared/dto/StyleCompRepository';
import { handleDownload } from '../../shared/util/download';
import downloadIcon from '../../asset/icon/download.png';
import { useAuth } from '../../shared/hooks/useApi/useAuth';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import { useModalStore } from '../../shared/store/portal/modalStore';
import fullIcon from '../../asset/icon/full_image.png';
import playIcon from '../../asset/icon/play.png';


interface WorkDetailImgProps {
  workDetailId: string;
  description: string;
  convertedCloudFrontUrl: string;
  allowDownload: boolean;
  priority: boolean;
  isVideo?: boolean;
  onPlay?: () => void;
}

const WorkDetailImg: React.FC<WorkDetailImgProps> = ({
  workDetailId,
  description,
  convertedCloudFrontUrl,
  allowDownload,
  priority,
  isVideo = false,
  onPlay,
}) => {
  const { isEditMode } = useEditMode();
  const { isOwner } = useAuth();
  const handleImageChange = useWorkViewStore((s) => s.updateImageActiveWorkDetailList);
  const { setStandardModal } = useModalStore();
  const [isHovered, setIsHovered] = useState(false);

  const showOriginImg = () => {
    setStandardModal({
      modalType: ModalType.ORIGIN_IMG,
      title: description,
      value: convertedCloudFrontUrl,
      handleChange: () => { },
      option: allowDownload
    });
  }


  return (
    <WorkDetailImgWrapper
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <OptimizedMoleculeImg
        srcUrl={convertedCloudFrontUrl}
        alt="Work Detail"
        displaySize={DisplaySize.REGULAR}
        displayAlignment={DisplayAlignment.CENTER}
        handleChange={(originUrl: string, width: number, height: number) =>
          handleImageChange(workDetailId, originUrl, width, height)
        }
        StyledImg={WorkImage as React.ComponentType<StyledImgProps>}
        priority={priority}
      />
      {!isEditMode && isVideo && onPlay && (
        <PlayOverlay $isHovered={isHovered}>
          <UniversalIconBtn
            size="standard"
            border={false}
            backgroundImg={playIcon}
            onClick={onPlay}
          />
        </PlayOverlay>
      )}
      {!isEditMode && !isVideo && (
      <BtnContainer $isHovered={isHovered}>  
        {convertedCloudFrontUrl && isOwner && (
          <UniversalIconBtn 
            size="fit" 
            border={true} 
            backgroundImg={downloadIcon} 
            onClick={() => handleDownload(convertedCloudFrontUrl, `${description}`)} />
        )}
        <UniversalIconBtn 
          size="fit" 
          border={true} 
          backgroundImg={fullIcon} 
          onClick={showOriginImg} />
      </BtnContainer>
      )}
    </WorkDetailImgWrapper>
  );
};

const WorkDetailImgWrapper = styled.div`
  position: relative;
  width: 100%;
  height: fit-content;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 4px;
`;

const WorkImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const PlayOverlay = styled.div<{ $isHovered: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  opacity: ${({ $isHovered }) => ($isHovered ? 1 : 0)};
  backdrop-filter: blur(${({ $isHovered }) => ($isHovered ? 1.5 : 0)}px);
  transition: all 0.4s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BtnContainer = styled.div<{ $isHovered: boolean }>`
  position: absolute;
  right: 0.5vh;
  top: 0.5vh;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5vh;
  opacity: ${({ $isHovered }) => $isHovered ? 1 : 0};
  cursor: ${({ $isHovered }) => $isHovered ? 'pointer' : 'none'};
  pointer-events: ${({ $isHovered }) => $isHovered ? 'auto' : 'none'};
  transition: all 0.3s ease-in-out;
`;


export default WorkDetailImg;
